<template>
  <div>
    <div class="text-xl font-semibold">
      <router-link
        v-slot="{ href, route, navigate }"
        :to="$router.currentRoute.query.redirect || '/cashregisters'"
        custom
      >
        <li class="list-none">
          <a :href="href" @click="navigate">{{ route.meta.name }}</a>
          <span v-if="cashregister"> / {{ cashregister.name }}</span>
        </li>
      </router-link>
    </div>
    <LoadingSpinner v-if="loading" />
    <div
      v-else-if="cashregister"
      class="container grid grid-cols-1 lg:grid-cols-2 gap-4"
    >
      <div class="lg:grid-cols-1">
        <div class="grid grid-cols-2 gap-4 mt-4">
          <BaseInput
            :value="cashregister.name"
            label="Namn"
            placeholder="Kassa 1"
            disabled
          />
          <BaseInput
            :value="activeControlUnitActivation.productionNumber"
            label="Produktionsnummer"
            disabled
          />
          <BaseInput
            :value="activeControlUnitActivation.apiKey"
            label="API-nyckel"
            disabled
          />
          <BaseInput
            :value="activeControlUnitActivation.controlUnitSerial"
            label="Kontrollenhet"
            disabled
          />
          <BaseInput
            v-if="cashregister.companyName || cashregister.companyId"
            :value="cashregister.companyName"
            label="Företag"
            disabled
          />
          <BaseInput :value="cashregister.street" label="Gata" disabled />
          <BaseInput
            :value="cashregister.zipcode"
            label="Postnummer"
            disabled
          />
          <BaseInput :value="cashregister.city" label="postort" disabled />
        </div>
      </div>

      <div>
        <div class="grid grid-cols-2 mt-4 gap-x-4 border-t">
          <!-- <div class="flex flex-row pt-3 mt-3 "> -->
          <BaseLabel class="mb-2 mt-2" label="Aktiveringar" />
          <button
            class="mb-2 mt-2 btn btn-outline-secondary"
            :disabled="loading"
            @click="$modal.show('create-cashregister-activation')"
          >
            Ny aktivering
          </button>
          <!-- </div> -->
        </div>

        <b-table
          :fields="activationFields"
          :items="cashregister.controlUnitActivations"
        />
      </div>
    </div>
    <modal
      name="create-cashregister-activation"
      transition="pop-out"
      :pivot-y="0.2"
      height="auto"
      adaptive
    >
      <div class="p-3">
        <BaseSelect
          v-model="form.controlUnitId"
          label="Kontrollenhet"
          class="mt-3"
          :options="controlUnitsOptions"
        />

        <BaseInput v-model="form.address" label="Gata" />
        <BaseInput v-model="form.postalCode" label="Postnummer" />
        <BaseInput v-model="form.city" label="Postort" />

        <div class="mt-5 text-right">
          <button
            class="btn btn-default"
            @click="$modal.hide('create-cashregister-activation')"
          >
            Avbryt
          </button>
          <button
            class="btn btn-primary"
            :disabled="
              !form.controlUnitId ||
                !form.address ||
                !form.postalCode ||
                !form.city ||
                createActiviationLoading
            "
            @click="createActivation"
          >
            Spara
          </button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import GET_CASHREGISTER from '@/graphql/Cashregister.gql'
import GET_CONTROLUNITS from '@/graphql/ControlUnits.gql'
import { useQuery, useResult, useMutation } from '@vue/apollo-composable'
import LoadingSpinner from '@/components/LoadingSpinner'
import { watch, computed, reactive } from '@vue/composition-api'
import gql from 'graphql-tag'

export default {
  components: { LoadingSpinner },
  setup(_, { root }) {
    const {
      result: cashregisterResult,
      loading,
      refetch: refetchCashregister,
    } = useQuery(
      GET_CASHREGISTER,
      { id: root.$route.params.id },
      { fetchPolicy: 'cache-and-network' }
    )

    const form = reactive({
      address: '',
      postalCode: '',
      city: '',
      controlUnitId: '',
    })

    const cashregister = useResult(
      cashregisterResult,
      {},
      data => data.cashregister
    )

    const {
      result: controlUnitsResult,
      refetch: refetchControlunits,
    } = useQuery(GET_CONTROLUNITS, {
      fetchPolicy: 'cache-and-network',
    })

    const controlunits = useResult(
      controlUnitsResult,
      [],
      data => data.controlunits
    )

    const activationFields = [
      {
        key: 'active',
        label: 'Aktiv',
        formatter: value => (value ? 'Ja' : 'Nej'),
      },
      { key: 'controlUnit.controlUnitSerial', label: 'Kontrollenhet' },
      {
        key: 'activationDate',
        label: 'Aktiverad',
        formatter: value => new Date(value).toLocaleDateString('sv-SE'),
      },
      { key: 'activationId', label: 'aktivering' },
      { key: 'company', label: 'Företag' },
    ]

    const activeControlUnitActivation = reactive({
      productionNumber: 'ingen aktivering',
      apiKey: 'ingen aktivering',
      controlUnitSerial: 'ingen aktivering',
    })

    watch(
      () => cashregister.value,
      () => {
        form.address = cashregister.value.street
        form.postalCode = cashregister.value.zipcode
        form.city = cashregister.value.city
        if (cashregister.value.controlUnitActivations.length > 0) {
          const activation = cashregister.value.controlUnitActivations.find(
            a => a.active
          )
          activeControlUnitActivation.productionNumber =
            activation.productionNumber
          activeControlUnitActivation.apiKey = activation.apiKey
          activeControlUnitActivation.controlUnitSerial =
            activation.controlUnit.controlUnitSerial
        }
      }
    )

    const controlUnitsOptions = computed(() => {
      if (!controlunits.value) return []
      return controlunits.value.map(controlunit => ({
        id: controlunit.id,
        name: `${controlunit.controlUnitSerial}${
          controlunit.default ? ' - standard' : ''
        }`,
      }))
    })

    const {
      mutate: createActivation,
      loading: createActiviationLoading,
    } = useMutation(
      gql`
        mutation CreateControlUnitActivation($data: CreteActivationInput!) {
          createControlUnitActivation(data: $data) {
            id
          }
        }
      `,
      () => ({
        variables: {
          data: {
            saloonId: cashregister.value.saloonId,
            companyId: cashregister.value.companyId,
            controlUnitId: form.controlUnitId,
            cashRegisterId: cashregister.value.id,
            address: form.address,
            postalCode: form.postalCode,
            city: form.city,
          },
        },
        update: () => {
          refetchCashregister()
          root.$modal.hide('create-cashregister-activation')
        },
      })
    )

    return {
      cashregister,
      createActivation,
      loading,
      activationFields,
      activeControlUnitActivation,
      form,
      controlUnitsOptions,
      createActiviationLoading,
    }
  },
}
</script>

<style lang="scss" scoped></style>
